import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { FONT_FAMILY_OPTIONS, ResolvedTypography, TypographySettings } from "@core/data/types/advanced-report";

/**
 * Per-axis inherit/override editor for one cascade tier (Page or Component). Font family shows its
 * effective value (this tier's override, else the inherited parent value), a badge saying which, and
 * a reset that drops back to inheriting. Emits only this tier's overrides.
 */
@Component({
    selector: 'pv-inheritable-typography',
    templateUrl: 'inheritable-typography.control.html',
    styleUrls: ['inheritable-typography.control.scss'],
    host: { class: 'pv-inheritable-typography' },
})
export class InheritableTypographyControl implements OnInit {

    // Resolved baseline from the parent tier, shown wherever this tier inherits.
    @Input() parent: ResolvedTypography;

    // This tier's overrides (absent family = inherit).
    @Input() value: TypographySettings = {};

    @Input() parentLabel = 'Inherited';

    @Output() valueChange = new EventEmitter<TypographySettings>();

    fontFilterControl: FormControl = new FormControl('');
    filteredFonts$: Observable<string[]>;

    ngOnInit() {
        this.filteredFonts$ = this.fontFilterControl.valueChanges.pipe(
            startWith(''),
            map(term => this._filterFonts(term)),
        );
    }

    get familyEffective(): string { return this.value?.fontFamily ?? this.parent?.fontFamily; }

    get isOverridden(): boolean {
        return this.value != null && this.value.fontFamily != null;
    }

    setFamily(family: string) {
        this._emit({ ...this.value, fontFamily: family });
    }

    reset() {
        const next: TypographySettings = { ...this.value };
        delete next.fontFamily;
        this._emit(next);
    }

    private _emit(next: TypographySettings) {
        this.value = next;
        this.valueChange.emit(next);
    }

    private _filterFonts(term: string): string[] {
        const search = (term || '').toLowerCase().trim();
        if (!search) return FONT_FAMILY_OPTIONS;
        return FONT_FAMILY_OPTIONS.filter(font => font.toLowerCase().includes(search));
    }
}
